import React, { useContext, useMemo, useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { I18nContext } from '@bingads-webui-react/i18n-context';
import {
  TextTitle,
  ValidatedPanel,
  getPanelErrorProps
} from '@bingads-webui-campaign/wizard-jumbo-card';
import { RadioGroup } from '@bingads-webui-react/radio';
import { Label } from '@bingads-webui-react/fabric-control';
import { advancedSettingsSectionStyle, toggleButtonStyle } from '@bingads-webui-campaign-react/common-styles';
import { creationCommonStyle } from '@bingads-webui-campaign-react/campaign-adgroup-settings-shared-styles';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { withCustomValidation } from '@bingads-webui-react/validation';

import classNames from 'classnames';
import _ from 'underscore';

import { PlacementImg } from '../media';
import { SectionShimmer } from '../shimmers';

const getStyles = (__, core) =>
  ({
    placementTargetSettingContainer: {
      ...creationCommonStyle(core),
      ...advancedSettingsSectionStyle(core),
      ...toggleButtonStyle(core),
      '& .title': {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '8px',
      },
      '& .intent-target-options': {
        display: 'flex',
        flexDirection: 'column',
        '& ul': {
          listStyle: 'none',
          padding: 0,
          margin: 0,
          '& .radio': {
            marginBottom: '15px',
          },
        },
      },
      '& .radio-group-option': {
        position: 'absolute',
        top: '3px',
        '&-description-text': {
          fontSize: '12px',
        },
      },
    },
  });

const RadioGroupOption = ({ titleString, descriptionString, updateSelection }) => {
  const [isSelected, setIsSelected] = useState(false);
  const titleStyle = {
    color: isSelected ? '#242424' : '#616161',
    fontSize: '16px',
  };

  useEffect(() => {
    const result = updateSelection();
    setIsSelected(result);
  }, [updateSelection]);

  return (
    <span>
      <span className={classNames('radio-group-option')}>
        <span style={titleStyle}>
          {titleString}
        </span>
        <div className={classNames('radio-group-option-description-text')}>
          {descriptionString}
        </div>
      </span>
    </span>
  );
};

RadioGroupOption.propTypes = {
  titleString: PropTypes.string.isRequired,
  descriptionString: PropTypes.string.isRequired,
  updateSelection: PropTypes.func.isRequired,
};

const PlacementTargetingSettingsInternal = (props) => {
  const i18n = useContext(I18nContext);
  const mobilePlacementsTitleString = i18n.getString(_TL_('All Microsoft mobile placements'));
  const mobilePlacementsDescriptionString = i18n.getString(_TL_('This option includes mobile Microsoft sites and additional partner traffic for extended reach and lower cost per click.'));
  const gamingPlacementsTitleString = i18n.getString(_TL_('Microsoft owned and operated mobile placements'));
  const gamingPlacementsDescriptionString = i18n.getString(_TL_('This option includes all Microsoft sites and select partner traffic with performance and conversion rates similar to that of Microsoft Bing.'));
  const classes = useDefaultStyles(getStyles);

  const { validationProps, updateValidation, setActiveKey } = props;
  const intentOptions = {
    Gaming: 'Gaming',
    Mobile: 'Mobile',
  };
  const activeKey = validationProps.data.placementTarget || intentOptions.Mobile;

  if (!validationProps.data.placementTarget) {
    updateValidation([], { placementTarget: activeKey });
  }


  const intentOptionsKeys = useMemo(() => (_.compact([
    intentOptions.Mobile,
    intentOptions.Gaming,
  ])), [intentOptions.Gaming, intentOptions.Mobile]);

  const updateSelection = useCallback(option => activeKey === option, [activeKey]);

  const intentOptionsKeysMap = useMemo(() => ({
    [intentOptions.Mobile]: (
      <RadioGroupOption
        titleString={mobilePlacementsTitleString}
        descriptionString={mobilePlacementsDescriptionString}
        updateSelection={() => updateSelection(intentOptions.Mobile)}
      />
    ),
    [intentOptions.Gaming]: (
      <RadioGroupOption
        titleString={gamingPlacementsTitleString}
        descriptionString={gamingPlacementsDescriptionString}
        updateSelection={() => updateSelection(intentOptions.Gaming)}
      />
    ),
  }), [intentOptions.Mobile, intentOptions.Gaming, mobilePlacementsTitleString, mobilePlacementsDescriptionString, gamingPlacementsTitleString, gamingPlacementsDescriptionString, updateSelection]);

  const onIntentOptionChanged = useCallback((key) => {
    setActiveKey(key);
    updateValidation([], { placementTarget: key });
  }, [setActiveKey, updateValidation]);

  return (
    <div className={classNames(classes.placementTargetSettingContainer)}>
      <div className={classNames('title')}>
        <Label required>{i18n.getString(_TL_('Where do you want to show your ads?'))}</Label>
      </div>
      <div className={classNames('intent-target-options')}>
        <ul role="radiogroup" aria-label={i18n.getString(_TL_('placement-targeting-radio-group'))}>
          <RadioGroup
            items={intentOptionsKeys}
            renderItem={key => intentOptionsKeysMap[key]}
            onChange={onIntentOptionChanged}
            checked={activeKey}
            getItemId={_.identity}
          />
        </ul>
      </div>
    </div>);
};

PlacementTargetingSettingsInternal.propTypes = {
  setActiveKey: PropTypes.func.isRequired,
  updateValidation: PropTypes.func.isRequired,
  validationProps: PropTypes.shape({
    data: PropTypes.shape({
      placementTarget: PropTypes.string,
    }),
  }),
};

PlacementTargetingSettingsInternal.defaultProps = {
  validationProps: {
    data: {
      placementTarget: 'Mobile',
    },
  },
};

const PlacementTargetSettings = withCustomValidation(PlacementTargetingSettingsInternal);

export const PlacementTargetPanel = (props) => {
  const classes = useDefaultStyles(getStyles);
  const [activeKey, setActiveKey] = useState('Mobile');

  const {
    isLoading,
    expand,
    ...rest
  } = props;
  return (
    <div className={classNames(classes.placementTargetSettingContainer)}>
      <ValidatedPanel
        key="placement-target"
        title={<TextTitle
          titleKey={_TL_('Placements')}
          topicKey="pop_BA_Apps_Placements"
          details={activeKey}
          icon={PlacementImg}
        />}
        attentionBeaconProps={{ getExtraProps: getPanelErrorProps }}
        actuationProps={{
          expand,
          expandKey: 'placement-target',
        }}
        {...rest}
      >
        {
        isLoading ?
          <SectionShimmer /> :
          <PlacementTargetSettings setActiveKey={setActiveKey} />
        }
      </ValidatedPanel>
    </div>
  );
};

PlacementTargetPanel.propTypes = {
  isLoading: PropTypes.bool,
  expand: PropTypes.func.isRequired,
  data: PropTypes.string,
};

PlacementTargetPanel.defaultProps = {
  isLoading: false,
  data: 'Mobile',
};
